/**
 * useInvoiceLifecycleSteps Hook
 *
 * Reads the automation settings of the invoice steps and turns them into the
 * step model both lifecycle layouts render.
 */
import { useQuery } from '@apollo/client/react';
import { ApprovalMode, DEFAULT_INVOICE_COMPLETION } from '@insio/shared-types';
import {
  ExtractionSettingsDocument,
  InvoiceApprovalSettingsDocument,
  InvoiceAutomationSettingsDocument,
  InvoiceAutofillSettingsDocument,
  InvoiceMatchConfirmationSettingsDocument,
  InvoiceValidationSettingsDocument
} from '@/graphql';
import { FeatureFlagKey, useFeatureFlag } from '@/features/feature-flags';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { APPROVAL_MODE_LABEL_KEY, isProcurementStep, type LifecycleStep } from './types';

export function useInvoiceLifecycleSteps(): LifecycleStep[] {
  const t = useFeatureTranslation('systemSettings.automation.lifecycle');
  const tClosure = useFeatureTranslation('systemSettings.automation.invoiceClosure');
  const isProcurementEnabled = useFeatureFlag(FeatureFlagKey.PROCUREMENT);

  const { data: autofillData } = useQuery(InvoiceAutofillSettingsDocument);
  const { data: matchingData } = useQuery(InvoiceMatchConfirmationSettingsDocument, {
    skip: !isProcurementEnabled
  });
  const { data: approvalData } = useQuery(InvoiceApprovalSettingsDocument);
  const { data: validationData } = useQuery(InvoiceValidationSettingsDocument);
  const { data: extractionData } = useQuery(ExtractionSettingsDocument);
  const { data: completionData } = useQuery(InvoiceAutomationSettingsDocument);

  // Defaults mirror the backend: line amounts autofill, approval and AI matching on even
  // when no config has ever been saved; AI validation off until enabled.
  const autofillSettings = autofillData?.invoiceAutofillSettings;
  const isAutofillEnabled = autofillSettings
    ? autofillSettings.isLineAmountsEnabled || autofillSettings.isAccountingTotalsEnabled
    : true;
  const isAiMatchingEnabled = matchingData?.invoiceMatchConfirmationSettings.isEnabled ?? true;
  const approvalMode = approvalData?.invoiceApprovalSettings.mode ?? ApprovalMode.AI_AND_USER;
  const isAiValidationEnabled = validationData?.invoiceValidationSettings.isEnabled ?? false;
  // Extraction is automatic until an account switches it off.
  const isAutoExtract = extractionData?.currentAccount?.extractionSettings.autoExtract ?? true;
  const completion = completionData?.invoiceAutomationSettings ?? DEFAULT_INVOICE_COMPLETION;
  // With nothing switched on an invoice is completed only by hand. The condition that leads the
  // summary takes its capitalised form.
  const completionSummary = [
    ...(completion.isApprovalRequired ? [tClosure('summary.approved')] : []),
    ...(completion.isPaymentRequired
      ? [tClosure(completion.isApprovalRequired ? 'summary.paid' : 'summary.paidOnly')]
      : []),
    ...(completion.isPostingRequired
      ? [
          tClosure(
            completion.isApprovalRequired || completion.isPaymentRequired
              ? 'summary.posted'
              : 'summary.postedOnly'
          )
        ]
      : [])
  ];

  const steps: LifecycleStep[] = [
    {
      key: 'extraction',
      icon: 'magnifying-glass',
      isLocked: false,
      isEnabled: isAutoExtract,
      stateLabel: isAutoExtract ? t('enabled') : t('manual')
    },
    {
      key: 'autofill',
      icon: 'pen-to-square',
      isLocked: false,
      isEnabled: isAutofillEnabled,
      stateLabel: isAutofillEnabled ? t('enabled') : t('disabled')
    },
    {
      key: 'matching',
      icon: 'link',
      isLocked: false,
      // The line matching always runs — only the AI part is switchable.
      isEnabled: true,
      stateLabel: isAiMatchingEnabled ? t('enabled') : t('enabledWithoutAi')
    },
    {
      key: 'validation',
      icon: 'list-ul',
      isLocked: false,
      // The rule validation always runs — only the AI part is switchable.
      isEnabled: true,
      stateLabel: isAiValidationEnabled ? t('enabled') : t('enabledWithoutAi')
    },
    {
      key: 'approval',
      icon: 'circle-check',
      isLocked: false,
      isEnabled: approvalMode !== ApprovalMode.SKIPPED,
      stateLabel: t(APPROVAL_MODE_LABEL_KEY[approvalMode])
    },
    {
      key: 'export',
      icon: 'arrow-right-from-bracket',
      isLocked: true,
      // Export follows the connected accounting system, not a setting of its own.
      stateLabel: t('byIntegration')
    },
    {
      key: 'closure',
      icon: 'box',
      isLocked: false,
      isEnabled: true,
      stateLabel:
        completionSummary.length > 0 ? completionSummary.join(' + ') : tClosure('summary.manual')
    }
  ];

  return isProcurementEnabled
    ? steps
    : steps.filter(step => !isProcurementStep({ document: 'invoice', step: step.key }));
}
